/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateMeetingPayload,
  MeetingListResponse,
  MeetingRequest,
  MeetingType,
} from '../types/meeting';
import {
  ApprovedMeeting,
  getAllMeeting,
  getMeetingById,
  getApprovedMeetingDetailsById,
  getMeetingByStatus,
  rejectMeeting,
  forwardMeeting,
  approveMeetingRequest,
} from '../service/meeting';
import { MeetingRequestData } from '../components/Meeting/ScheduleMeeting';
import { ApprovedMeetingInterface } from '../types/approvedMeeting';

export const useMeeting = () => {
  return useQuery<MeetingType[]>({
    queryKey: ['meetings'],
    queryFn: getAllMeeting,
  });
};

export const useMeetingByStatus = (
  status: string,
  page: number = 1,
  limit: number = 10,
  userId?: string
) => {
  return useQuery<MeetingListResponse>({
    queryKey: ['meetingsByStatus', status, page, limit, userId],
    queryFn: async () => {
      const result = await getMeetingByStatus({ status, page, limit, userId });
      // Ensure we always return an array
      return result;
    },
    enabled: !!status,
  });
};

export const useMeetingById = (meetingId: number) => {
  return useQuery<MeetingRequestData>({
    queryKey: ['meeting', meetingId],
    queryFn: async () => {
      const meeting = await getMeetingById(meetingId);
      return meeting; // Return the meeting directly as it's not an array
    },
    enabled: !!meetingId, // Avoid fetching if meetingId is undefined/null
  });
};

export const useAPProvedMeeting = (value: CreateMeetingPayload, id: number) => {
  return useQuery<MeetingRequest>({
    queryKey: ['meeting', id],
    // @ts-expect-error
    queryFn: async () => {
      const meeting = await ApprovedMeeting(value, id);
      return meeting; // Return the meeting directly as it's not an array
    },
  });
};

export const useApprovedMeetingDetailsById = (id: number) => {
  return useQuery<ApprovedMeetingInterface>({
    queryKey: ['approved-meeting', id],
    queryFn: async () => {
      const meeting = await getApprovedMeetingDetailsById(id);
      return meeting;
    },
    enabled: !!id && id > 0,
  });
};

export const useRejectMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      const meeting = await rejectMeeting(id);
      return meeting;
    },
    onSuccess: () => {
      // Invalidate meeting lists so UI updates immediately
      queryClient.invalidateQueries({ queryKey: ['meetingsByStatus'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
};

// export const useForwardMeeting = () => {
//   const queryClient = useQueryClient();
//   return useMutation<
//     void,
//     Error,
//     {
//       sendEmail: boolean;
//       id: number;
//       notes: string;
//     }
//   >({
//     mutationFn: async ({
//       sendEmail,
//       id,
//       notes,
//     }: {
//       sendEmail: boolean;
//       id: number;
//       notes: string;
//     }) => {
//       await forwardMeeting({ sendEmail, id, notes });
//     },
//     onSuccess: () => {
//       // Invalidate meeting lists so UI updates immediately
//       queryClient.invalidateQueries({ queryKey: ['meetingsByStatus'] });
//       queryClient.invalidateQueries({ queryKey: ['meetings'] });
//     },
//   });
// };

export const useForwardMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    Error,
    {
      id: number;
      payload: any; // or ForwardMeetingSubmitDto
    }
  >({
    mutationFn: async ({ id, payload }) => {
      return await forwardMeeting({ id, payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetingsByStatus'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
};



export const useApproveMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    Error,
    {
      id: number;
      payload: import('../types/meeting').ApproveMeetingPayload;
    }
  >({
    mutationFn: async ({ id, payload }) => {
      return await approveMeetingRequest(id, payload);
    },
    onSuccess: () => {
      // Auto-refresh related lists
      queryClient.invalidateQueries({ queryKey: ['meetingsByStatus'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
};
